import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { AlertNotice, SimpleModal, ModalInfo } from 'shared/components/index';
import * as roomActions from 'features/room-builder/duck';
import * as seatingChartActions from 'features/seating-chart/duck';
import SeatingDetails from './SeatingDetails';
import './SeatingDetails.scss';

const seatingDetails = {
  seatingRowCount: '',
  seatingColumnCount: '',
  writingSurfaceWidth: '',
  writingSurfaceDepth: '',
  distanceBetweenShoulders: '',
  distanceBetweenHeads: '',
};

const checkRegSummaries = (regSummaries, updatedRoom, room) => {
  const currentRowCount = +room.seatingRowCount;
  const updatedRowCount = +updatedRoom.seatingRowCount;
  const currentColumnCount = +room.seatingColumnCount;
  const updatedColumnCount = +updatedRoom.seatingColumnCount;

  if (updatedRowCount < currentRowCount) {
    for (let i = updatedRowCount; i < currentRowCount; i++) {
      if (regSummaries[i] && regSummaries[i].length) {
        return true;
      }
    }
    return false;
  }

  if (updatedColumnCount < currentColumnCount) {
    for (let i = 0; i < currentRowCount; i++) {
      if (regSummaries[i] && regSummaries[i].length) {
        for (let j = updatedColumnCount; j < currentColumnCount; j++) {
          if (regSummaries[i][j]) {
            return true;
          }
        }
      }
    }
    return false;
  }

  return false;
};

class ModalSeatingDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      room: {
        ...seatingDetails,
        ...props.room,
      },
      warningSeatsModalOpen: false,
      discardModalOpen: false,
      roomUpdated: {},
      errors: {},
    };

    this.child = React.createRef();
    this.onSubmit = this.onSubmit.bind(this);
    this.closeWarningModal = this.closeWarningModal.bind(this);
    this.closeSeatingModal = this.closeSeatingModal.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      !this.state.room.id &&
      !this.props.room.loading &&
      !isEmpty(this.props.room) &&
      prevProps.room !== this.props.room
    ) {
      this.setState({
        room: this.props.room,
      });
    }
  }

  async onSubmit() {
    const { actions, regSummaries, room } = this.props;

    if (this.child.current && this.child.current.onSubmit) {
      const roomContent = this.child.current.onSubmit();

      if (roomContent && !isEmpty(roomContent)) {
        if (
          regSummaries &&
          regSummaries.length &&
          checkRegSummaries(regSummaries, roomContent, room)
        ) {
          this.setState({
            warningSeatsModalOpen: true,
            roomUpdated: roomContent,
          });
          return;
        }
        const updatedRoom = await actions.updateRoom(roomContent);
        if (updatedRoom && updatedRoom.id) {
          sessionStorage.setItem(
            'successSavedMessage',
            this.props.t('seatingChart.seatingDetails.successMessage')
          );
          actions.getRegSummaries(updatedRoom.id);
          this.closeSeatingModal();
        }
      }
      return;
    }
  }

  async closeWarningModal(submitData) {
    const { roomUpdated } = this.state;

    if (submitData) {
      const updatedRoom = await this.props.actions.updateRoom(roomUpdated);
      if (updatedRoom && updatedRoom.id) {
        this.props.actions.getRegSummaries(updatedRoom.id);
        this.closeSeatingModal(true);
      }
    }
    this.setState({
      warningSeatsModalOpen: false,
      roomUpdated: {},
    });
  }

  closeSeatingModal() {
    this.props.actions.resetErrorRoomAction();

    if (this.child.current && this.child.current.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
      return;
    }
    this.props.closeModal();
  }

  closeDiscardModal(discardChangesFlag) {
    this.setState({
      discardModalOpen: false,
    });

    if (discardChangesFlag) {
      this.props.closeModal();
    }
  }

  render() {
    const { room, roomStatus, modalOpen, errorRoom, tcEvent } = this.props;
    const { warningSeatsModalOpen, discardModalOpen } = this.state;

    return (
      <div>
        <ModalInfo
          modalIsOpen={modalOpen}
          closeModal={() => this.closeSeatingModal()}
          headerTitle="seatingChart.seatingDetails.modalTitle"
          onSubmit={this.onSubmit}
          saveLabel="common.labels.save"
          className="modal-seating-details">
          <div>
            <h4>{room.name}</h4>
            <div>{!isEmpty(tcEvent) && tcEvent.testingEvent.name}</div>
            <p>
              <span>{roomStatus.locationName}</span>
              {roomStatus.locationArea && roomStatus.locationArea.length > 0 && (
                <span>, {roomStatus.locationArea}</span>
              )}{' '}
              - <span>{roomStatus.testCenterName}</span>
            </p>
            <SeatingDetails ref={this.child} room={room} required />
            {!isEmpty(errorRoom) && (
              <div className="mt-3">
                <AlertNotice message={this.props.t('errors.somethingWentWrong')} type="error" />
              </div>
            )}
          </div>
        </ModalInfo>
        <SimpleModal
          isOpen={warningSeatsModalOpen}
          onCancel={() => this.closeWarningModal(false)}
          onConfirm={() => this.closeWarningModal(true)}
          confirmLabel={this.props.t('common.labels.save')}
          cancelLabel={this.props.t('common.labels.back')}
          headline={this.props.t('seatingChart.removeSeatsModalTitle')}
        />
        <SimpleModal
          isOpen={discardModalOpen}
          onCancel={() => this.closeDiscardModal(false)}
          onConfirm={() => this.closeDiscardModal(true)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('seatingChart.discardModal.title')}
          message={this.props.t('seatingChart.discardModal.message')}
          buttonsReverseOrder
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    room: state.roomOptions.room,
    errorRoom: state.roomOptions.errorRoom,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...roomActions,
        ...seatingChartActions,
      },
      dispatch
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(undefined, { withRef: true })(ModalSeatingDetails));

ModalSeatingDetails.propTypes = {
  room: PropTypes.shape({}).isRequired,
  regSummaries: PropTypes.arrayOf(PropTypes.array),
  t: PropTypes.func.isRequired,
};

ModalSeatingDetails.defaultProps = {};
